import { call, all, put, takeEvery } from 'redux-saga/effects';

import URLs from '../../../constants/urls';
import { apiPython as api, apiRephrase } from '../../../service/api';

import { getSessionStorage } from '../../../utils/helpers';

import {
  deleteQuestionTQBAction,
  getEditQuestionDataTQBAction,
  getQuestionDetailsForPreviewTQBAction,
  getRephraseSuggestionsTQBAction,
  getTechnicalQBAction,
  saveQuestionTQBAction,
  saveQuestionsFromExcelAction,
  setEditQuestionDataTQB,
  setExpertiseListTQB,
  setImportQuestionLoader,
  setQuestionDetailsTQB,
  setRephraseDataTQB,
  setSkillListTQB,
  setTQBErrorMessage,
  setTQBSuccessMessage,
  setTQBToast,
  setTechnicalQBData,
  setTechnicalQBLoader
} from '../../features/technicalQuestionBank/technicalQuestionBankSlice';
import {
  buildRankingRanksFromCorrectOrder,
  complexityToLabel,
  normalizeDropdownOptionsForEdit,
  parseQuestionMetaForForm,
  summarizeQuestionBankImportResults,
} from '../../../utils/questionBankUtils';
import { unwrapApiPayload } from '../../../utils/apiPayload';
import moment from 'moment';

const getApiErrorMessage = (error) => error?.response?.data?.message || error?.response?.data?.detail || 'Something went wrong';

export function* getTechnicalQBSaga(action) {
  yield put(setTechnicalQBLoader(true));

  try {
    const response = yield api.get(URLs.GET_TECHNICAL_QUESTION_BANK);

    const responseData = response.data.data;
    if (responseData?.isAccessDenied || responseData?.IsAccessDenied) {
      yield put(setTechnicalQBData([]));
      yield put(setSkillListTQB(responseData?.SkillArea ?? responseData?.skillArea ?? []));
      yield put(setExpertiseListTQB(responseData?.ExpertiesLevel ?? responseData?.expertiesLevel ?? []));
      yield put(setTechnicalQBLoader(false));
      yield put(setTQBErrorMessage('Question bank access is restricted for your admin domain.'));
      return;
    }

    // API returns PascalCase keys — normalize to camelCase for the table
    const skillArea = responseData.SkillArea ?? responseData.skillArea ?? [];

    // Build a flat skill id -> name lookup from the nested skill tree
    const buildSkillMap = (skills, map = {}) => {
      skills.forEach((s) => {
        map[s.id ?? s.Id] = s.skillName ?? s.SkillName;
        if (s.secondarySkill?.length) buildSkillMap(s.secondarySkill, map);
      });
      return map;
    };
    const skillMap = buildSkillMap(skillArea);

    const questionsList = responseData.QuestionsList ?? responseData.questionsList ?? [];
    const data = questionsList.map((item) => ({
      id: item.Id ?? item.id,
      prompt: item.Prompt ?? item.prompt,
      skill_Id: item.Skill_Id ?? item.skill_Id,
      skills: skillMap[item.Skill_Id ?? item.skill_Id] ?? '',
      complexity: item.Complexity ?? item.complexity,
      lastEditedOn: moment(item.LastEditedOn ?? item.lastEditedOn).format('DD-MMM-YYYY'),
      // keep original fields for edit/preview use
      ...item,
    }));

    yield put(setTechnicalQBData(data));
    yield put(setSkillListTQB(skillArea));
    yield put(setExpertiseListTQB(responseData.ExpertiesLevel ?? responseData.expertiesLevel ?? []));
    yield put(setTechnicalQBLoader(false));
  } catch (error) {
    yield put(setTechnicalQBLoader(false));
    yield put(setTQBErrorMessage(getApiErrorMessage(error)));
  }
}

export function* getQuestionDetailsForPreviewTQBSaga(action) {
  yield put(setTechnicalQBLoader(true));

  try {
    const response = yield api.get(`${URLs.GET_QUESTION_DETAILS_FOR_PREVIEW}?questionId=${action.payload}`);

    const data = response.data.data;
    yield put(setQuestionDetailsTQB(data));
    yield put(setTechnicalQBLoader(false));
  } catch (error) {
    yield put(setTechnicalQBLoader(false));
    yield put(setTQBErrorMessage(getApiErrorMessage(error)));
  }
}

export function* getEditQuestionDataTQBSaga(action) {
  yield put(setTechnicalQBLoader(true));

  try {
    const response = yield api.get(`${URLs.GET_EDIT_QUESTION_DATA}?id=${action.payload}`);

    let skill = null;
    const details = response.data?.data?.details ?? {};
    const skillsList = details.skillArea ?? [];
    const skillIdRaw = details.skill_id ?? details.skillId ?? details.Skill_Id;
    const skillId = skillIdRaw != null && skillIdRaw !== '' ? Number(skillIdRaw) : null;

    const idEq = (a, b) => String(a) === String(b);

    const getSkill = (nodes) => {
      if (!Array.isArray(nodes)) return;
      for (const skillItem of nodes) {
        const sid = skillItem.id ?? skillItem.Id;
        if (skillId != null && idEq(sid, skillId)) {
          skill = skillItem;
          return;
        }
        if (skillItem.secondarySkill?.length) {
          getSkill(skillItem.secondarySkill);
          if (skill) return;
        }
      }
    };
    getSkill(skillsList);

    const q = details.question ?? {};
    const rawOpts = Array.isArray(q.options) ? q.options : Array.isArray(q.Options) ? q.Options : [];
    const questionType = q.questionType ?? q.QuestionType;
    let normalizedOpts = rawOpts.map((o) => ({
      optionPrompt: o.optionPrompt ?? o.OptionPrompt ?? '',
      isCorrectOption: !!(o.isCorrectOption ?? o.IsCorrectOption)
    }));
    if (Number(questionType) === 5) {
      normalizedOpts = normalizeDropdownOptionsForEdit(normalizedOpts, questionType);
    }

    const skillLabel = skill?.skillName ?? skill?.SkillName ?? '';
    const meta = parseQuestionMetaForForm(q);
    const data = {
      ...q,
      questionPrompt: q.questionPrompt ?? q.QuestionPrompt ?? '',
      questionType: q.questionType ?? q.QuestionType,
      complexity: complexityToLabel(details.complexity ?? q.complexity ?? q.Complexity) || details.complexity,
      options: [...normalizedOpts, { isCorrectOption: false, optionPrompt: '' }],
      skill: { label: skillLabel, value: skillId },
      questionId: q.questionId ?? q.QuestionId ?? action.payload,
      questionMeta: {
        points: meta.points,
        show_work: meta.show_work,
        workInstructions: meta.workInstructions,
        correctAnswer: meta.correctAnswer,
      },
      rankingRanks: buildRankingRanksFromCorrectOrder(normalizedOpts, meta.correctOrder),
    };
    yield put(setEditQuestionDataTQB(data));
    yield put(setSkillListTQB(skillsList));
    yield put(setExpertiseListTQB(details.expertiesLevel ?? details.ExpertiesLevel ?? []));
    yield put(setTechnicalQBLoader(false));
  } catch (error) {
    yield put(setTechnicalQBLoader(false));
    yield put(setTQBErrorMessage(getApiErrorMessage(error)));
  }
}

export function* getRephraseDataTQBSaga(action) {
  yield put(setTechnicalQBLoader(true));

  try {
    const response = yield apiRephrase.post('', { question: action.payload, type: 'Rew' });
    const suggestions = typeof response.data === 'string' ? JSON.parse(response.data) : response. Data;
    yield put(setRephraseDataTQB({ actualText: action.payload, suggestions }));
    yield put(setRephraseDataTQB({ actualText: action.payload, suggestions: JSON.parse(response.data.data) }));
    yield put(setTechnicalQBLoader(false));
  } catch (error) {
    yield put(setTechnicalQBLoader(false));
    yield put(setTQBErrorMessage(error?.response?.data?.message));
  }
}

export function* saveQuestionTQBSaga(action) {
  yield put(setTechnicalQBLoader(true));
  const { onSuccess, data } = action.payload;
  try {
    const raw = data && typeof data === 'object' ? { ...data } : {};
    const skillVal = raw.SkillId ?? raw.skillId;
    const qid = raw.QuestionId ?? raw.questionId ?? 0;
    const body = {
      loggedUserID: raw.loggedUserID ?? raw.LoggedUserID,
      SkillId: skillVal != null && skillVal !== '' ? Number(skillVal) : 0,
      Prompt: raw.Prompt ?? raw.prompt ?? '',
      QuestionType: Number(raw.QuestionType ?? raw.questionType ?? 0),
      Complexity: String(raw.Complexity ?? raw.complexity ?? ''),
      QuestionOption: (raw.QuestionOption ?? raw.questionOption ?? []).map((o) => ({
        OptionText: o.OptionText ?? o.optionText ?? '',
        IsCorrectOption: !!(o.IsCorrectOption ?? o.isCorrectOption)
      })),
      QuestionId: Number(qid) || 0,
      ImageType: raw.ImageType ?? raw.imageType ?? 'noImage',
      FileBytes: Array.isArray(raw.FileBytes) ? raw.FileBytes : Array.isArray(raw.fileBytes) ? raw.fileBytes : [],
      QuestionMeta: raw.QuestionMeta ?? raw.questionMeta ?? null,
      CorrectOrder: raw.CorrectOrder ?? raw.correctOrder ?? null,
      PlaceholderText: raw.PlaceholderText ?? raw.placeholderText ?? null,
    };
    const hasFile = (body.FileBytes || []).some((x) => x && String(x).trim());
    body.ImageType = hasFile ? (raw.ImageType ?? raw.imageType ?? 'newImage') : 'noImage';

    const response = yield api.post(`${URLs.SET_SAVEQUESTION}`, body);

    const envelope = response.data;
    if (envelope?.status === 'failed' || (envelope?.statusCode && envelope.statusCode >= 400)) {
      yield put(setTQBErrorMessage(envelope?.message || 'Save failed'));
      yield put(setTechnicalQBLoader(false));
      return;
    }

    onSuccess && onSuccess();
    yield put(setEditQuestionDataTQB({}));
    yield put(
      setRephraseDataTQB({
        actualText: '',
        suggestions: []
      })
    );

    yield put(setTQBSuccessMessage('Saved Successfully'));
    yield put(getTechnicalQBAction());
    yield put(setTechnicalQBLoader(false));
  } catch (error) {
    yield put(setTechnicalQBLoader(false));
    yield put(setTQBErrorMessage(getApiErrorMessage(error)));
  }
}

export function* saveQuestionsFromExcelSaga(action) {
  yield put(setImportQuestionLoader(true));
  const { onSuccess, data } = action.payload;
  try {
    const actualPayload = {
      loggedUserID: getSessionStorage('USER_ID'),
      questionBankImports: data
    };
    const response = yield api.post(`${URLs.SAVE_QUESTIONS_FROM_EXCEL}`, actualPayload);

    const results = unwrapApiPayload(response.data);
    const summary = summarizeQuestionBankImportResults(Array.isArray(results) ? results : []);

    yield put(setTQBToast(summary));

    if (summary.savedCount > 0) {
      yield put(getTechnicalQBAction());
      onSuccess && onSuccess();
    }

    yield put(setImportQuestionLoader(false));
  } catch (error) {
    yield put(setImportQuestionLoader(false));
    yield put(setTQBErrorMessage(getApiErrorMessage(error)));
  }
}

export function* deleteQuestionTQBSaga(action) {
  yield put(setTechnicalQBLoader(true));
  const { id, onSuccess } = action.payload;
  try {
    const actualPayload = {
      userSamId: getSessionStorage('USER_ID'),
      id: id
    };
    const response = yield api.post(`${URLs.DELETE_QUESTION_TQB}`, actualPayload);
    if (response?.data?.status === 'Success') {
      yield put(getTechnicalQBAction());
      if (onSuccess) onSuccess();
      yield put(setTQBSuccessMessage('Deleted Successfully'));
    }

    yield put(setTechnicalQBLoader(false));
  } catch (error) {
    yield put(setTechnicalQBLoader(false));
    yield put(setTQBErrorMessage(getApiErrorMessage(error)));
  }
}

export function* watchTechnicalQuestionBank() {
  yield takeEvery(getTechnicalQBAction.type, getTechnicalQBSaga);
  yield takeEvery(getQuestionDetailsForPreviewTQBAction.type, getQuestionDetailsForPreviewTQBSaga);
  yield takeEvery(getEditQuestionDataTQBAction.type, getEditQuestionDataTQBSaga);
  yield takeEvery(getRephraseSuggestionsTQBAction.type, getRephraseDataTQBSaga);
  yield takeEvery(saveQuestionTQBAction.type, saveQuestionTQBSaga);
  yield takeEvery(saveQuestionsFromExcelAction.type, saveQuestionsFromExcelSaga);
  yield takeEvery(deleteQuestionTQBAction.type, deleteQuestionTQBSaga);
}

export default function* technicalQuestionBankSaga() {
  yield all([watchTechnicalQuestionBank()]);
}
